import React, { useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ConfigProvider, Spin } from 'antd';
import { GanttProvider, useGantt } from './context/GanttContext';
import GanttToolbar from './components/GanttToolbar';
import TaskListPanel, { TaskListPanelRef } from './components/TaskListPanel';
import TimelinePanel, { TimelinePanelRef } from './components/TimelinePanel';
import { CustomGanttChartProps, FlattenedTask, FULLSCREEN_Z } from './types/gantt.types';

interface GanttContentProps {
  onTaskClick?: (task: FlattenedTask) => void;
}

const GanttContent: React.FC<GanttContentProps> = ({ onTaskClick }) => {
  const { loading, isFullscreen, setIsFullscreen } = useGantt();
  const taskListRef = useRef<TaskListPanelRef>(null);
  const timelineRef = useRef<TimelinePanelRef>(null);
  const isScrollingRef = useRef<'table' | 'timeline' | null>(null);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when in fullscreen
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isFullscreen, setIsFullscreen]);

  // Sync scroll between task list and timeline - prevent infinite loop
  const handleTaskListScroll = useCallback((scrollTop: number) => {
    if (isScrollingRef.current === 'timeline') return;
    isScrollingRef.current = 'table';
    timelineRef.current?.scrollTo(scrollTop);
    setTimeout(() => {
      isScrollingRef.current = null;
    }, 50);
  }, []);

  const handleTimelineScroll = useCallback((scrollTop: number) => {
    if (isScrollingRef.current === 'table') return;
    isScrollingRef.current = 'timeline';
    taskListRef.current?.scrollTo(scrollTop);
    setTimeout(() => {
      isScrollingRef.current = null;
    }, 50);
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: isFullscreen ? '100vh' : 500,
        }}
      >
        <Spin size="large" tip="Loading Gantt chart..." />
      </div>
    );
  }

  const ganttContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'calc(100vh - 240px)',
        minHeight: 400,
        background: '#fff',
        border: isFullscreen ? 'none' : '1px solid #d9d9d9',
        borderRadius: isFullscreen ? 0 : 4,
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? FULLSCREEN_Z.content : 'auto',
      }}
    >
      {/* Toolbar */}
      <div style={{ flexShrink: 0 }}>
        <GanttToolbar />
      </div>

      {/* Main content area - side by side panels */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Task List (left panel) */}
        <TaskListPanel
          ref={taskListRef}
          onScroll={handleTaskListScroll}
        />

        {/* Timeline (right panel) */}
        <TimelinePanel
          ref={timelineRef}
          onScroll={handleTimelineScroll}
          onTaskClick={onTaskClick}
        />
      </div>
    </div>
  );

  // When fullscreen, render via portal to document body. The ConfigProvider
  // lifts every antd popup raised from inside the chart above the fullscreen
  // surface — without it, tooltips (antd default 1070) hide behind it.
  if (isFullscreen) {
    return ReactDOM.createPortal(
      <ConfigProvider theme={{ token: { zIndexPopupBase: FULLSCREEN_Z.popupBase } }}>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: FULLSCREEN_Z.backdrop,
          }}
          onClick={() => setIsFullscreen(false)}
        />
        {ganttContent}
      </ConfigProvider>,
      document.body
    );
  }

  return ganttContent;
};

const CustomGanttChart: React.FC<CustomGanttChartProps> = ({
  projectId,
  groupBy,
  onTaskClick,
}) => {
  return (
    <GanttProvider projectId={projectId} groupBy={groupBy}>
      <GanttContent onTaskClick={onTaskClick} />
    </GanttProvider>
  );
};

export default CustomGanttChart;
